import React, { useEffect, useMemo, useState } from 'react';
import { Archive, CheckCircle2, Copy, Download, RefreshCw, Undo2, UserCheck } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { canImpersonate } from '../../constants/adminRoles';
import {
  reloadToAccountHome,
  setImpersonationReturnPath,
} from '../admin/impersonationNav';
import {
  getFeedbackAuthorInfo,
  markFeedbackTriaged,
  setFeedbackStatus,
  subscribeRecentFeedback,
} from '../../services/feedbackService';

function normalizeCategory(cat) {
  if (cat === 'bug' || cat === 'improvement' || cat === 'feature') return cat;
  return 'improvement';
}

function categoryLabel(cat) {
  const c = normalizeCategory(cat);
  if (c === 'bug') return 'Bug / problem';
  if (c === 'feature') return 'New feature';
  return 'Improvement';
}

function pickConsoleExcerpt(consoleLogTail, maxLines = 10) {
  if (!Array.isArray(consoleLogTail) || consoleLogTail.length === 0) return [];
  const tail = consoleLogTail.slice(Math.max(0, consoleLogTail.length - maxLines));
  return tail.map((e) => {
    const ts = typeof e?.ts === 'number' ? new Date(e.ts).toISOString() : '';
    const level = e?.level || '';
    const args = Array.isArray(e?.args) ? e.args : [];
    let msg = '';
    try {
      msg = args.map((a) => (typeof a === 'string' ? a : JSON.stringify(a))).join(' ');
    } catch {
      msg = String(args);
    }
    return `- ${ts} ${level} ${msg}`.trim();
  });
}

function buildCursorPrompt(row) {
  const id = row?.id || '';
  const cat = normalizeCategory(row?.category);
  const title = categoryLabel(cat);
  const url = row?.context?.url || '';
  const step = row?.context?.currentStep || '';
  const propertyId = row?.context?.propertyId || '';
  const userAgent = row?.context?.userAgent || '';
  const viewport = row?.context?.viewport ? JSON.stringify(row.context.viewport) : '';
  const message = row?.message || '';
  const screenshot = row?.screenshotDownloadUrl || '';
  const consoleCount = Array.isArray(row?.consoleLogTail) ? row.consoleLogTail.length : 0;
  const consoleExcerpt = pickConsoleExcerpt(row?.consoleLogTail, 10);

  const ask =
    cat === 'bug'
      ? [
          '- Propose steps to reproduce based on context.',
          '- Form a hypothesis for the root cause.',
          '- Implement a minimal fix and note impacted files.',
          '- If relevant, consider Firebase rules/auth and client-side errors.',
        ]
      : [
          '- Clarify expected behavior and edge cases.',
          '- Propose a small implementation plan (files to touch).',
          '- Implement the change with minimal risk.',
        ];

  return [
    `## ${title}${step ? ` — ${step}` : ''}`,
    '',
    `**FeedbackId**: ${id}`,
    url ? `**URL**: ${url}` : '',
    step ? `**Step**: ${step}` : '',
    propertyId ? `**PropertyId**: ${propertyId}` : '',
    userAgent ? `**UserAgent**: ${userAgent}` : '',
    viewport ? `**Viewport**: ${viewport}` : '',
    '',
    '### User message',
    message ? message : '(empty)',
    '',
    screenshot ? `### Screenshot\n${screenshot}\n` : '',
    consoleCount > 0
      ? [
          `### Console logs (${consoleCount} events, excerpt)`,
          ...consoleExcerpt,
          '',
        ].join('\n')
      : '',
    '### What I want you to do',
    ...ask,
    '',
  ]
    .filter(Boolean)
    .join('\n');
}

async function copyText(text) {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // fallback
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', 'true');
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand('copy');
  ta.remove();
  if (!ok) throw new Error('copy_failed');
}

function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function FeedbackAdminPage() {
  const { t } = useLanguage();
  const { startImpersonation, adminRole } = useAuth();
  const canStartImpersonation = canImpersonate(adminRole || 'super_admin');
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState('');
  const [actionBusyById, setActionBusyById] = useState({});
  const [copyMsgById, setCopyMsgById] = useState({});
  const [authorByUid, setAuthorByUid] = useState({});

  useEffect(() => {
    setErr('');
    setActionErr('');
    setBusy(true);
    const unsub = subscribeRecentFeedback(
      { pageSize: 200 },
      (r) => {
        setRows(r);
        setBusy(false);
      },
      (e) => {
        console.error('Feedback admin subscribe error:', e);
        setErr(String(e?.message || e || 'error'));
        setBusy(false);
      },
    );
    return () => unsub?.();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const missing = Array.from(
      new Set(
        rows
          .map((r) => String(r.authorUid || '').trim())
          .filter((uid) => uid && !authorByUid[uid]),
      ),
    );
    if (missing.length === 0) return () => {};
    (async () => {
      const pairs = await Promise.all(
        missing.map(async (uid) => [uid, await getFeedbackAuthorInfo(uid)]),
      );
      if (cancelled) return;
      setAuthorByUid((prev) => {
        const next = { ...prev };
        for (const [uid, info] of pairs) next[uid] = info;
        return next;
      });
    })().catch((e) => {
      console.error('Feedback author load error:', e);
    });
    return () => {
      cancelled = true;
    };
  }, [rows, authorByUid]);

  const setRowStatusLocal = (id, nextStatus) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === id
          ? {
              ...r,
              status: nextStatus,
              // best-effort local indicator; snapshot will eventually reflect serverTimestamp.
              triagedAt: nextStatus === 'triaged' ? r.triagedAt || new Date() : null,
            }
          : r,
      ),
    );
  };

  const handleToggleTriaged = async (row) => {
    const id = row?.id;
    if (!id) return;
    setActionErr('');
    setActionBusyById((m) => ({ ...m, [id]: true }));
    const next = row.status === 'triaged' ? 'new' : 'triaged';
    // optimistic UI
    setRowStatusLocal(id, next);
    try {
      await markFeedbackTriaged(id, next === 'triaged');
    } catch (e) {
      console.error('Feedback triage update error:', e);
      // rollback optimistic update
      setRowStatusLocal(id, row.status || 'new');
      setActionErr(t('feedbackAdmin.actionError'));
    } finally {
      setActionBusyById((m) => ({ ...m, [id]: false }));
    }
  };

  const handleToggleArchived = async (row) => {
    const id = row?.id;
    if (!id) return;
    setActionErr('');
    setActionBusyById((m) => ({ ...m, [id]: true }));
    const next = row.status === 'archived' ? 'triaged' : 'archived';
    setRowStatusLocal(id, next);
    try {
      await setFeedbackStatus(id, next);
    } catch (e) {
      console.error('Feedback archive update error:', e);
      setRowStatusLocal(id, row.status || 'new');
      setActionErr(t('feedbackAdmin.actionError'));
    } finally {
      setActionBusyById((m) => ({ ...m, [id]: false }));
    }
  };

  const exportRows = useMemo(
    () =>
      rows.map((r) => ({
        id: r.id,
        category: r.category || '',
        message: r.message || '',
        createdAt: r.createdAt?.toDate?.()?.toISOString?.() || null,
        authorUid: r.authorUid || '',
        status: r.status || 'new',
        triagedAt: r.triagedAt?.toDate?.()?.toISOString?.() || null,
        archivedAt: r.archivedAt?.toDate?.()?.toISOString?.() || null,
        screenshotStoragePath: r.screenshotStoragePath || '',
        screenshotDownloadUrl: r.screenshotDownloadUrl || '',
        includeConsole: !!r.includeConsole,
        consoleLogTail: Array.isArray(r.consoleLogTail) ? r.consoleLogTail : [],
        context: r.context && typeof r.context === 'object' ? r.context : {},
      })),
    [rows],
  );

  const filteredRows = useMemo(() => {
    if (statusFilter === 'all') return rows;
    return rows.filter((r) => (r.status || 'new') === statusFilter);
  }, [rows, statusFilter]);

  const setCopyMsg = (id, msg) => {
    setCopyMsgById((m) => ({ ...m, [id]: msg }));
    setTimeout(() => {
      setCopyMsgById((m) => {
        const next = { ...m };
        delete next[id];
        return next;
      });
    }, 1500);
  };

  const handleCopyPrompt = async (row) => {
    const id = row?.id;
    if (!id) return;
    try {
      await copyText(buildCursorPrompt(row));
      setCopyMsg(id, t('feedbackAdmin.copied'));
    } catch (e) {
      console.error('Copy cursor prompt error:', e);
      setCopyMsg(id, t('feedbackAdmin.copyFailed'));
    }
  };

  const handleImpersonate = async (row) => {
    const uid = String(row?.authorUid || '').trim();
    if (!uid) return;
    setActionErr('');
    setActionBusyById((m) => ({ ...m, [row.id]: true }));
    try {
      await startImpersonation({ targetUid: uid });
      setImpersonationReturnPath('/admin/feedback');
      reloadToAccountHome();
    } catch (e) {
      console.error('startImpersonation error:', e);
      setActionErr("Échec de l'impersonation : " + (e?.message || e));
      setActionBusyById((m) => ({ ...m, [row.id]: false }));
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold text-slate-900">{t('feedbackAdmin.title')}</h1>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => downloadJson(`feedback-export-${new Date().toISOString().slice(0, 10)}.json`, exportRows)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
        >
          <Download className="w-4 h-4" />
          <span className="text-sm font-semibold">{t('feedbackAdmin.exportJson')}</span>
        </button>
      </div>

      {err ? (
        <div className="text-sm text-red-600">{err}</div>
      ) : null}
      {actionErr ? (
        <div className="text-sm text-red-600">{actionErr}</div>
      ) : null}

      <div className="text-xs text-slate-500 flex items-center gap-2">
        <RefreshCw className={busy ? 'w-4 h-4 animate-spin' : 'w-4 h-4'} />
        <span>{busy ? t('loading') : `${t('feedbackAdmin.loaded')} ${filteredRows.length} / ${rows.length}`}</span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {[
          { id: 'all', label: t('feedbackAdmin.filterAll') },
          { id: 'new', label: t('feedbackAdmin.filterNew') },
          { id: 'triaged', label: t('feedbackAdmin.filterTriaged') },
          { id: 'archived', label: t('feedbackAdmin.filterArchived') },
        ].map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => setStatusFilter(opt.id)}
            className={[
              'px-3 py-1.5 rounded-full text-xs font-semibold border',
              statusFilter === opt.id
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50',
            ].join(' ')}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filteredRows.length === 0 ? (
          <div className="border border-slate-200 rounded-xl bg-white p-4 text-slate-500 text-sm">
            {t('feedbackAdmin.empty')}
          </div>
        ) : (
          filteredRows.map((r) => {
            const when = r.createdAt?.toDate?.()?.toLocaleString?.() || '';
            const consoleCount = Array.isArray(r.consoleLogTail) ? r.consoleLogTail.length : 0;
            const authorInfo = authorByUid[String(r.authorUid || '').trim()] || null;
            const authorLabel =
              String(r.authorDisplayName || '').trim() ||
              String(r.authorEmail || '').trim() ||
              authorInfo?.displayName ||
              authorInfo?.email ||
              r.authorUid ||
              t('feedbackAdmin.unknownAuthor');
            const status = r.status || 'new';
            const statusClass =
              status === 'archived'
                ? 'bg-slate-100 text-slate-700'
                : status === 'triaged'
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-emerald-100 text-emerald-800';
            return (
              <article key={r.id} className="border border-slate-200 rounded-xl bg-white p-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${statusClass}`}>
                    {status}
                  </span>
                  <span className="text-xs text-slate-500">{when}</span>
                  <span className="text-xs text-slate-400">•</span>
                  <span className="text-xs text-slate-600">{categoryLabel(r.category)}</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div className="md:col-span-2 space-y-2">
                    <p className="whitespace-pre-wrap break-words text-slate-900">{r.message || '—'}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                      {r.context?.url ? (
                        <a
                          href={r.context.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-indigo-600 hover:underline"
                        >
                          {t('feedbackAdmin.openPage')}
                        </a>
                      ) : null}
                      {r.screenshotDownloadUrl ? (
                        <a
                          href={r.screenshotDownloadUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-indigo-600 hover:underline"
                        >
                          {t('feedbackAdmin.openScreenshot')}
                        </a>
                      ) : null}
                    </div>
                  </div>
                  <div className="space-y-1 text-xs text-slate-600">
                    <div>
                      <span className="font-semibold">{t('feedbackAdmin.colAuthor')}:</span>{' '}
                      <span className="break-all">{authorLabel}</span>
                    </div>
                    {(String(r.authorEmail || '').trim() || authorInfo?.email) &&
                    (String(r.authorDisplayName || '').trim() || authorInfo?.displayName) ? (
                      <div className="text-slate-500 break-all">
                        {String(r.authorEmail || '').trim() || authorInfo?.email}
                      </div>
                    ) : null}
                    <div>
                      <span className="font-semibold">{t('feedbackAdmin.colConsole')}:</span>{' '}
                      {consoleCount > 0 ? `${consoleCount} ${t('feedbackAdmin.consoleEvents')}` : '—'}
                    </div>
                    {consoleCount > 0 ? (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-indigo-600 hover:underline select-none">
                          {t('feedbackAdmin.showConsole')}
                        </summary>
                        <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2 max-h-56 overflow-auto">
                          <pre className="text-[11px] leading-5 whitespace-pre-wrap break-words text-slate-700">
                            {pickConsoleExcerpt(r.consoleLogTail, 50).join('\n')}
                          </pre>
                        </div>
                      </details>
                    ) : null}
                    {r.id ? (
                      <div className="text-slate-400 break-all">#{r.id}</div>
                    ) : null}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {r.authorUid && canStartImpersonation ? (
                    <button
                      type="button"
                      onClick={() => handleImpersonate(r)}
                      disabled={!!actionBusyById[r.id]}
                      className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-60"
                      title="Se connecter en tant que cet utilisateur"
                    >
                      <UserCheck className="w-4 h-4" />
                      <span className="text-xs font-semibold">
                        {actionBusyById[r.id] ? '…' : 'Se connecter en tant que'}
                      </span>
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => handleCopyPrompt(r)}
                    className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-60"
                    title={t('feedbackAdmin.copyCursorPrompt')}
                  >
                    <Copy className="w-4 h-4" />
                    <span className="text-xs font-semibold">
                      {copyMsgById[r.id] || t('feedbackAdmin.copyCursorPrompt')}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleTriaged(r)}
                    className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-60"
                    disabled={!!actionBusyById[r.id] || status === 'archived'}
                    title={status === 'triaged' ? t('feedbackAdmin.markNew') : t('feedbackAdmin.markTriaged')}
                  >
                    {status === 'triaged' ? (
                      <Undo2 className="w-4 h-4" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4" />
                    )}
                    <span className="text-xs font-semibold">
                      {actionBusyById[r.id]
                        ? t('feedbackAdmin.working')
                        : status === 'triaged'
                          ? t('feedbackAdmin.untriage')
                          : t('feedbackAdmin.triage')}
                    </span>
                  </button>
                  {(status === 'triaged' || status === 'archived') ? (
                    <button
                      type="button"
                      onClick={() => handleToggleArchived(r)}
                      className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-60"
                      disabled={!!actionBusyById[r.id]}
                      title={status === 'archived' ? t('feedbackAdmin.unarchive') : t('feedbackAdmin.archive')}
                    >
                      <Archive className="w-4 h-4" />
                      <span className="text-xs font-semibold">
                        {actionBusyById[r.id]
                          ? t('feedbackAdmin.working')
                          : status === 'archived'
                            ? t('feedbackAdmin.unarchive')
                            : t('feedbackAdmin.archive')}
                      </span>
                    </button>
                  ) : null}
                </div>
              </article>
            );
          })
        )}
      </div>
    </div>
  );
}

